import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { MenuItem } from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  IosShareOutlined as ExportIcon,
  RefreshOutlined as RefreshIcon,
  Visibility as ViewIcon,
  ListAlt as ListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';
import ExportOtpDialog from '../components/ExportOtpDialog';

async function downloadTextFile({ filename, content, mime = 'text/csv;charset=utf-8', extensions }) {
  try {
    if (typeof window !== 'undefined' && window.showSaveFilePicker) {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'File',
            accept: { [String(mime).split(';')[0]]: Array.isArray(extensions) ? extensions : ['.csv'] },
          },
        ],
      });
      const writable = await handle.createWritable();
      const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
      await writable.write(blob);
      await writable.close();
      return;
    }
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) return;
  }
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCsvValue(v) {
  const raw = String(v ?? '');
  const trimmed = raw.trimStart();
  const safe = trimmed && /^[=+\-@]/.test(trimmed) ? `'${raw}` : raw;
  if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
    return `"${safe.replaceAll('"', '""')}"`;
  }
  return safe;
}

export default function HardcopySheetReport() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const pagePerm = permissionsMap.hardcopy_sheet_report || {};
  const canUpdate = isSuperAdmin || Boolean(pagePerm.can_update);
  const canDelete = isSuperAdmin || Boolean(pagePerm.can_delete);
  const canImport = isSuperAdmin || Boolean(pagePerm.can_import);
  const canExport = isSuperAdmin || Boolean(pagePerm.can_export);

  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('billDate');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterEway, setFilterEway] = useState('');
  const [viewRecord, setViewRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  
  // Dropdown Data
  const [appCodes, setAppCodes] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [firmList, setFirmList] = useState([]);
  const [articleList, setArticleList] = useState([]);

  const loadDropdowns = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: token ? `Bearer ${token}` : '' };
      
      const [resCodes, resArticles, resVendors] = await Promise.all([
        fetch('/api/application-codes', { headers }).then(r => r.json()),
        fetch('/api/articles', { headers }).then(r => r.json()),
        fetch('/api/vendors', { headers }).then(r => r.json())
      ]);

      setAppCodes(Array.isArray(resCodes.items) ? resCodes.items : []);
      setArticleList(Array.isArray(resArticles.items) ? resArticles.items : []);
      
      const allVendors = Array.isArray(resVendors.items) ? resVendors.items : [];
      
      const vendors = allVendors.filter(i => String(i.firmVendor || '').trim().toLowerCase() === 'vendor');
      const firms = allVendors.filter(i => String(i.firmVendor || '').trim().toLowerCase() === 'firm');

      const uniqueVendors = Array.from(new Map(vendors.map(item => [String(item.firmVendorCode || '').trim(), item])).values());
      const uniqueFirms = Array.from(new Map(firms.map(item => [String(item.firmVendorCode || '').trim(), item])).values());

      uniqueVendors.sort((a, b) => String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || '')));
      uniqueFirms.sort((a, b) => String(a.firmVendorCode || '').localeCompare(String(b.firmVendorCode || '')));

      setVendorList(uniqueVendors);
      setFirmList(uniqueFirms);
    } catch (e) {
      console.error('Failed to load dropdowns', e);
    }
  };

  const loadRows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: token ? `Bearer ${token}` : '' };

      const resSingle = await fetch(`${API_BASE_URL}/api/hardcopy-sheets`, { headers });
      const dataSingle = await resSingle.json();
      const singleItems = Array.isArray(dataSingle?.items) ? dataSingle.items : [];

      const processedSingleRows = singleItems.map(i => ({
        ...i,
        isMulti: false,
        subItems: [i],
        ewayBill: (String(i.ewayBill || '').toLowerCase() === 'yes' || i.ewayBill === 'Required') ? 'Required' : 'Not Required'
      }));

      const dedupKey = (r) =>
        [
          String(r.billDate || '').slice(0, 10),
          String(r.vendorCode || ''),
          String(r.firmCode || ''),
          String(r.invoiceNumber || ''),
          String(r.article || ''),
          String(r.gstPct ?? ''),
          String(r.billAmount ?? ''),
          String(r.freightCharges ?? ''),
          String(r.taxableAmount ?? ''),
          String(r.qty ?? ''),
          String(r.cgst ?? ''),
          String(r.sgst ?? ''),
          String(r.igst ?? ''),
          String(r.ewayBill || ''),
          String(r.rate ?? ''),
        ].join('|');

      const seen = new Set();
      const dedupedRows = [];
      for (const r of processedSingleRows) {
        const k = dedupKey(r);
        if (seen.has(k)) continue;
        seen.add(k);
        dedupedRows.push(r);
      }

      const allRows = [...dedupedRows];
      
      // Default sort by date desc
      allRows.sort((a, b) => new Date(b.billDate) - new Date(a.billDate));

      setRows(allRows);
    } catch (e) {
      console.error(e);
      setToast({ open: true, message: 'Failed to load sheets', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadRows();
  }, []);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const copy = rows.slice();
    copy.sort((a, b) => {
      let av = a?.[orderBy];
      let bv = b?.[orderBy];
      
      // Handle numbers
      if (['billAmount', 'taxableAmount'].includes(orderBy)) {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      }

      if (av === bv) return 0;
      if (order === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });
    return copy;
  }, [rows, orderBy, order]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sd = startDate ? new Date(startDate) : null;
    const ed = endDate ? new Date(endDate) : null;
    const userAssociations = user?.association || [];

    return sortedRows.filter((r) => {
      const dateStr = r.billDate ? String(r.billDate).slice(0, 10) : '';
      const d = dateStr ? new Date(dateStr) : null;
      if (sd && d && d < sd) return false;
      if (ed && d && d > ed) return false;
      if (filterUser && r.userCode !== filterUser) return false;
      if (filterEway && r.ewayBill !== filterEway) return false;

      // Filter by association
      if (userAssociations.length > 0 && !userAssociations.includes(r.vendorCode)) return false;

      if (!q) return true;
      return [r.userCode, r.billDate, r.invoiceNumber, r.article]
        .map((x) => String(x || '').toLowerCase())
        .some((s) => s.includes(q));
    });
  }, [sortedRows, query, startDate, endDate, filterUser, filterEway, user]);

  const uniqueOptions = useMemo(() => {
    const users = new Set();
    const eways = new Set();
    const userAssociations = user?.association || [];

    rows.forEach((r) => {
      if (userAssociations.length > 0 && !userAssociations.includes(r.vendorCode)) return;

      if (r.userCode) users.add(r.userCode);
      if (r.ewayBill) eways.add(r.ewayBill);
    });
    const ewayVals = Array.from(eways);
    if (ewayVals.length === 0) {
      ewayVals.push('Required', 'Not Required');
    }
    return {
      users: Array.from(users).sort(),
      eway: ewayVals.sort(),
    };
  }, [rows, user]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);
  
  const handleDelete = async (row) => {
    if (!canDelete) {
      setToast({ open: true, message: 'You do not have delete permission for this page', severity: 'error' });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: token ? `Bearer ${token}` : '' };
      const confirmDel = window.confirm('Delete selected record?');
      if (!confirmDel) return;
      if (!row?.id) {
        setToast({ open: true, message: 'Missing id', severity: 'error' });
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/hardcopy-sheets/${row.id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ open: true, message: 'Deleted successfully', severity: 'success' });
        loadRows();
      } else {
        setToast({ open: true, message: data.error || 'Delete failed', severity: 'error' });
      }
    } catch (e) {
      setToast({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };

  const performExportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const safeText = (v) => {
        const raw = String(v ?? '');
        const trimmed = raw.trimStart();
        return trimmed && /^[=+\-@]/.test(trimmed) ? `'${raw}` : raw;
      };
      const data = rows.map((r) => ({
        'User Code': safeText(r.userCode),
        'Bill Date': String(r.billDate).slice(0, 10),
        'Invoice Number': safeText(r.invoiceNumber),
        'Article': safeText(r.article),
        'Taxable Amount': r.taxableAmount,
        'Bill Amount': r.billAmount,
        'CGST': r.cgst,
        'SGST': r.sgst,
        'IGST': r.igst,
        'E-Way Bill': safeText(r.ewayBill),
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'HardcopySheets');
      const arrayBuf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([arrayBuf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      await downloadTextFile({
        filename: 'hardcopy_sheets_report.xlsx',
        content: blob,
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extensions: ['.xlsx'],
      });
    } catch (e) {
      setToast({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  // State to track pending export
  const [pendingExportType, setPendingExportType] = useState(null);

  const performExportCsv = async () => {
    const headers = [
      'User Code',
      'Bill Date',
      'Invoice Number',
      'Article',
      'Taxable Amount',
      'Bill Amount',
      'CGST',
      'SGST',
      'IGST',
      'E-Way Bill',
    ];
    const lines = [headers.join(',')];
    for (const r of rows) {
      const vals = [
        toCsvValue(r.userCode),
        toCsvValue(String(r.billDate).slice(0, 10)),
        toCsvValue(r.invoiceNumber),
        toCsvValue(r.article),
        toCsvValue(r.taxableAmount),
        toCsvValue(r.billAmount),
        toCsvValue(r.cgst),
        toCsvValue(r.sgst),
        toCsvValue(r.igst),
        toCsvValue(r.ewayBill),
      ];
      lines.push(vals.join(','));
    }
    await downloadTextFile({
      filename: 'hardcopy_sheets_report.csv',
      content: `${lines.join('\n')}\n`,
      mime: 'text/csv;charset=utf-8',
      extensions: ['.csv'],
    });
  };

  const handleExportCsv = async () => {
    if (!canExport) {
      setToast({
        open: true,
        message: 'You do not have export permission for this page',
        severity: 'error',
      });
      return;
    }
    if (isSuperAdmin) {
      await performExportCsv();
    } else {
      setPendingExportType('csv');
      setOtpDialogOpen(true);
    }
  };

  const handleExportExcel = async () => {
    if (!canExport) {
      setToast({
        open: true,
        message: 'You do not have export permission for this page',
        severity: 'error',
      });
      return;
    }
    if (isSuperAdmin) {
      await performExportExcel();
    } else {
      setPendingExportType('excel');
      setOtpDialogOpen(true);
    }
  };

  const onOtpSuccess = async () => {
    if (pendingExportType === 'excel') {
      await performExportExcel();
    } else if (pendingExportType === 'csv') {
      await performExportCsv();
    }
    setPendingExportType(null);
    setOtpDialogOpen(false);
  };


  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!canImport) {
      setToast({ open: true, message: 'You do not have import permission for this page', severity: 'error' });
      e.target.value = '';
      return;
    }

    try {
      setLoading(true);
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const items = jsonData.map((row) => ({
        userCode: row['User Code'] || row['userCode'],
        billDate: row['Bill Date'] || row['billDate'],
        vendorCode: row['From Vendor Code'] || row['vendorCode'],
        firmCode: row['To Firm Code'] || row['firmCode'],
        invoiceNumber: row['Invoice Number'] || row['invoiceNumber'],
        article: row['Article'] || row['article'],
        gstPct: row['GST %'] || row['gstPct'],
        billAmount: row['Bill Amount'] || row['billAmount'],
        freightCharges: row['Freight Charges'] || row['freightCharges'],
        taxableAmount: row['Taxable Amount'] || row['taxableAmount'],
        qty: row['Qty'] || row['qty'],
        cgst: row['CGST'] || row['cgst'],
        sgst: row['SGST'] || row['sgst'],
        igst: row['IGST'] || row['igst'],
        ewayBill: row['E-Way Bill'] || row['ewayBill'],
        rate: row['Rate'] || row['rate'],
      }));

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/hardcopy-sheets/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ items }),
      });
      const result = await res.json();
      
      if (result.imported !== undefined) {
        setToast({ 
          open: true, 
          message: `Imported: ${result.imported}, Skipped: ${result.skipped}`, 
          severity: 'success' 
        });
        loadRows();
      } else {
         setToast({ open: true, message: 'Import failed', severity: 'error' });
      }
    } catch (error) {
      console.error(error);
      setToast({ open: true, message: 'Import failed', severity: 'error' });
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const ViewModal = () => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    if (!viewRecord) return null;

    const subItems = viewRecord.subItems || [];
    const totalQty = subItems.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);
    const totalRate = subItems.reduce((sum, item) => sum + (Number(item.rate) || 0), 0);

    return (
      <Dialog
        fullScreen={fullScreen}
        open={Boolean(viewRecord)}
        onClose={() => setViewRecord(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Record Details</Typography>
          <IconButton onClick={() => setViewRecord(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Basic Information</Typography>
              <Stack spacing={1.5}>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 140 }}>Application Code:</Typography>
                  <Typography>{viewRecord.userCode}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 140 }}>Date:</Typography>
                  <Typography>{viewRecord.billDate ? String(viewRecord.billDate).slice(0, 10) : ''}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 140 }}>From Vendor:</Typography>
                  <Typography>{viewRecord.vendorCode}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 140 }}>To Firm:</Typography>
                  <Typography>{viewRecord.firmCode}</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Invoice Details</Typography>

              <Stack spacing={1.5}>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>Invoice No:</Typography>
                  <Typography>{viewRecord.invoiceNumber}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>Article:</Typography>
                  <Typography>{viewRecord.article}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>Qty:</Typography>
                  <Typography>{totalQty}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>Rate:</Typography>
                  <Typography>₹{totalRate.toFixed(2)}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Amount Details</Typography>
              <Stack spacing={1.5}>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 180 }}>Taxable Amount:</Typography>
                  <Typography>₹{viewRecord.taxableAmount}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 180 }}>Bill Amount:</Typography>
                  <Typography>₹{viewRecord.billAmount}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 180 }}>Freight & Forwarding Charges / Loading & Unloading:</Typography>
                  <Typography>₹{Number(viewRecord.freightCharges || 0).toFixed(2)}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 180 }}>GST %:</Typography>
                  <Typography>
                     {Array.from(new Set(subItems.map(i => `${Number(i.gstPct).toFixed(2)}%`))).join(', ')}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>GST Breakdown</Typography>

              <Stack spacing={1.5}>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>CGST:</Typography>
                  <Typography>₹{viewRecord.cgst}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>SGST:</Typography>
                  <Typography>₹{viewRecord.sgst}</Typography>
                </Box>
                <Box display="flex">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>IGST:</Typography>
                  <Typography>₹{viewRecord.igst}</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography fontWeight="bold" sx={{ width: 100 }}>E-Way Bill:</Typography>
                  {viewRecord.ewayBill === 'Required' ? (
                    <Chip label="Required" color="warning" size="small" sx={{ fontWeight: 'bold', height: 24 }} />
                  ) : (
                    <Typography>Not Required</Typography>
                  )}
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Article-wise GST Breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Article</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>GST %</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Taxable Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Bill Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>CGST</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>SGST</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>IGST</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{item.article}</TableCell>
                    <TableCell>{Number(item.qty).toFixed(2)}</TableCell>
                    <TableCell>{Number(item.gstPct).toFixed(2)}%</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>₹{Number(item.taxableAmount).toFixed(2)}</TableCell>
                    <TableCell>₹{Number(item.billAmount).toFixed(2)}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>₹{Number(item.cgst).toFixed(2)}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>₹{Number(item.sgst).toFixed(2)}</TableCell>
                    <TableCell>₹{Number(item.igst).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  };

   const EditModal = () => {
     const theme = useTheme();
     const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
     const [form, setForm] = useState({
       userCode: '',
       billDate: '',
       vendorCode: '',
       firmCode: '',
       invoiceNumber: '',
       article: '',
       gstPct: '',
       billAmount: '',
       freightCharges: '',
       taxableAmount: '',
       qty: '',
       cgst: '',
       sgst: '',
       igst: '',
       ewayBill: '',
       rate: '',
     });
 
     useEffect(() => {
       if (editRecord) {
         const uc = String(editRecord.userCode || '').trim();
         const vc = String(editRecord.vendorCode || '').trim();
         const fc = String(editRecord.firmCode || '').trim();
         const art = String(editRecord.article || '').trim();
         setForm({
             ...editRecord,
             userCode: uc,
             vendorCode: vc,
             firmCode: fc,
             article: art,
             gstPct: String(editRecord.gstPct ?? ''),
             billAmount: String(editRecord.billAmount ?? ''),
             freightCharges: String(editRecord.freightCharges ?? ''),
             taxableAmount: String(editRecord.taxableAmount ?? ''),
             qty: String(editRecord.qty ?? ''),
             cgst: String(editRecord.cgst ?? ''),
             sgst: String(editRecord.sgst ?? ''),
             igst: String(editRecord.igst ?? ''),
             ewayBill: String(editRecord.ewayBill ?? ''),
             rate: String(editRecord.rate ?? ''),
             billDate: editRecord.billDate ? String(editRecord.billDate).slice(0, 10) : '',
         });
       }
     }, [editRecord]);
 
     const handleChange = (field) => (e) => {
       const value = e.target.value;
      setForm((prev) => {
        const next = { ...prev, [field]: value };
        
        let newGstPct = next.gstPct;

        if (field === 'article') {
          const selectedArticle = articleList.find(a => a.item === value);
          if (selectedArticle) {
            newGstPct = String(selectedArticle.gstRatio || '');
            next.gstPct = newGstPct;
          }
        }

        // Recalculate taxable amount if billAmount OR gstPct changes
        // Formula: (Bill Amount * 100) / (100 + GST%)
        const bill = Number(next.billAmount) || 0;
        const gstVal = Number(newGstPct) || 0;
        
        if (bill > 0) {
          const denominator = 100 + gstVal;
          const calcTaxable = (bill * 100) / denominator;
          next.taxableAmount = String(calcTaxable.toFixed(2));
        } else if (field === 'billAmount') {
          next.taxableAmount = '';
        }

        // Calculate taxes based on Vendor GSTIN
        const currentVendorCode = field === 'vendorCode' ? value : next.vendorCode;
        const currentVendor = vendorList.find(v => v.firmVendorCode === currentVendorCode);
        const vendorGst = currentVendor?.gstNumber || '';
        const isIntraState = vendorGst.startsWith('06');
        
        const taxableVal = Number(next.taxableAmount) || 0;
        const gstRate = Number(next.gstPct) || 0;
        
        const totalTax = taxableVal * (gstRate / 100);
        
        if (isIntraState) {
          // CGST + SGST (half each)
          const halfTax = totalTax / 2;
          next.cgst = halfTax > 0 ? halfTax.toFixed(2) : '';
          next.sgst = halfTax > 0 ? halfTax.toFixed(2) : '';
          next.igst = '';
        } else {
          // IGST (full)
          next.cgst = '';
          next.sgst = '';
          next.igst = totalTax > 0 ? totalTax.toFixed(2) : '';
        }

        const taxable = Number(next.taxableAmount) || 0;
        const isRequired = taxable >= 50000;
        next.ewayBill = isRequired ? 'Required' : 'Not Required';
        
        // Rate Calculation
        const qty = Number(next.qty) || 0;
        if (qty > 0 && taxable > 0) {
            next.rate = String((taxable / qty).toFixed(2));
        }

        return next;
      });
    };

    const handleUpdate = async () => {
        if (!canUpdate) {
            setToast({ open: true, message: 'You do not have update permission for this page', severity: 'error' });
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/hardcopy-sheets/${form.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : '',
                },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (res.ok) {
                setToast({ open: true, message: 'Updated successfully', severity: 'success' });
                setEditRecord(null);
                loadRows();
            } else {
                setToast({ open: true, message: data.error || 'Update failed', severity: 'error' });
            }
        } catch (e) {
            console.error(e);
            setToast({ open: true, message: 'Update failed', severity: 'error' });
        }
    };

    if (!editRecord) return null;

    return (
      <Dialog
        fullScreen={fullScreen}
        open={Boolean(editRecord)}
        onClose={() => setEditRecord(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Edit Record</Typography>
          <IconButton onClick={() => setEditRecord(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                columnGap: 4,
                rowGap: 3,
              }}
            >
              <TextField
                label="Application User Code"
                select={isSuperAdmin}
                value={form.userCode}
                onChange={handleChange('userCode')}
                variant="outlined"
                fullWidth
                size="small"
                disabled={!isSuperAdmin}
              >
                {!appCodes.some(c => String(c.code).trim() === String(form.userCode || '').trim()) && form.userCode && (
                  <MenuItem value={form.userCode}>{form.userCode}</MenuItem>
                )}
                {appCodes.map((c) => (
                  <MenuItem key={c.id} value={c.code}>
                    {c.code}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Date of Bill Received"
                type="date"
                value={form.billDate}
                onChange={handleChange('billDate')}
                variant="outlined"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="From Vendor Code"
                select
               value={form.vendorCode}
               onChange={handleChange('vendorCode')}
               variant="outlined"
               fullWidth
               size="small"
             >
                {!vendorList.some(v => String(v.firmVendorCode).trim() === String(form.vendorCode || '').trim()) && form.vendorCode && (
                  <MenuItem value={form.vendorCode}>{form.vendorCode}</MenuItem>
                )}
                {vendorList.map((v) => (
                  <MenuItem key={v.id} value={v.firmVendorCode}>
                    {v.firmVendorCode}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="To Firm Code"
                select
               value={form.firmCode}
               onChange={handleChange('firmCode')}
               variant="outlined"
               fullWidth
               size="small"
             >
                {!firmList.some(f => String(f.firmVendorCode).trim() === String(form.firmCode || '').trim()) && form.firmCode && (
                  <MenuItem value={form.firmCode}>{form.firmCode}</MenuItem>
                )}
                {firmList.map((f) => (
                  <MenuItem key={f.id} value={f.firmVendorCode}>
                    {f.firmVendorCode}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Article"
                select
               value={form.article}
               onChange={handleChange('article')}
               variant="outlined"
               fullWidth
               size="small"
             >
                {!articleList.some(a => String(a.item).trim() === String(form.article || '').trim()) && form.article && (
                  <MenuItem value={form.article}>{form.article}</MenuItem>
                )}
                {articleList.map((a) => (
                  <MenuItem key={a.id} value={a.item}>
                    {a.item}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Applicable GST %"
                value={form.gstPct}
                onChange={handleChange('gstPct')}
                variant="outlined"
                fullWidth
                size="small"
              />
              <TextField
                label="Invoice Number"
                value={form.invoiceNumber}
                onChange={handleChange('invoiceNumber')}
                variant="outlined"
                fullWidth
                size="small"
              />
              <TextField
                label="Bill Amount"
                value={form.billAmount}
                onChange={handleChange('billAmount')}
                variant="outlined"
                fullWidth
                size="small"
              />
              <TextField
                label="Freight & Charges"
                value={form.freightCharges}
                onChange={handleChange('freightCharges')}
                variant="outlined"
                fullWidth
                size="small"
              />
              <TextField
                label="Taxable Amount"
                value={form.taxableAmount}
                // onChange={handleChange('taxableAmount')} // Calculated automatically
                InputProps={{ readOnly: true }}
                variant="filled"
                fullWidth
                size="small"
              />
              <TextField
                label="Qty"
                value={form.qty}
                onChange={handleChange('qty')}
                variant="outlined"
                fullWidth
                size="small"
              />
              <TextField
                label="Rate"
                value={form.rate}
                // onChange={handleChange('rate')} // Calculated
                InputProps={{ readOnly: true }}
                variant="filled"
                fullWidth
                size="small"
              />
               <TextField
                label="CGST"
                value={form.cgst}
                InputProps={{ readOnly: true }}
                variant="filled"
                fullWidth
                size="small"
              />
               <TextField
                label="SGST"
                value={form.sgst}
                InputProps={{ readOnly: true }}
                variant="filled"
                fullWidth
                size="small"
              />
               <TextField
                label="IGST"
                value={form.igst}
                InputProps={{ readOnly: true }}
                variant="filled"
                fullWidth
                size="small"
              />
               <TextField
                label="E-Way Bill"
                value={form.ewayBill}
                InputProps={{ readOnly: true }}
                variant="filled"
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => setEditRecord(null)}>Cancel</Button>
                <Button variant="contained" onClick={handleUpdate} disabled={!canUpdate}>Update</Button>
            </Box>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Layout title="Hardcopy Sheet Report">
      <Box sx={{ maxWidth: '100%', px: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 650, letterSpacing: -0.3 }}>
              Hardcopy Sheet Report
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              View and export hardcopy sheet data.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={loadRows} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            <Button startIcon={<ExportIcon />} variant="contained" color="success" onClick={handleExportExcel} disabled={loading || !canExport}>
              Export Excel
            </Button>
            <Button startIcon={<DownloadIcon />} variant="contained" color="info" onClick={handleExportCsv} disabled={loading || !canExport}>
              Export CSV
            </Button>
          </Stack>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search"
              sx={{ minWidth: 220, flex: '1 1 220px' }}
            />
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            />
            <TextField
              label="Application Code"
              select
              size="small"
              value={filterUser}
              onChange={(e) => {
                setFilterUser(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {uniqueOptions.users.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="E-Way Bill"
              select
              size="small"
              value={filterEway}
              onChange={(e) => {
                setFilterEway(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 160, flex: '1 1 160px' }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {uniqueOptions.eway.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setQuery('');
                setStartDate('');
                setEndDate('');
                setFilterUser('');
                setFilterEway('');
                setPage(0);
              }}
              sx={{ ml: 'auto' }}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            borderColor: (t) =>
              t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#eef2ff'),
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={1.25}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 750 }}>Records</Typography>
                <Chip
                  size="small"
                  label={
                    rows.length === filteredRows.length ? `${rows.length} total` : `${filteredRows.length} filtered`
                  }
                />
              </Stack>
              <TextField
                size="small"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                placeholder="Search by Application Code, Invoice No, or Article..."
                sx={{ minWidth: 280 }}
              />
            </Stack>
          </Box>

          <TableContainer>
            <Table
              size="small"
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#212121'),
                  color: (t) => (t.palette.mode === 'dark' ? 'common.white' : '#fff'),
                  fontWeight: 800,
                  py: 1.5,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {[
                    { id: 'id', label: 'ID' },
                    { id: 'userCode', label: 'Application Code' },
                    { id: 'billDate', label: 'Date' },
                    { id: 'invoiceNumber', label: 'Invoice No' },
                    { id: 'article', label: 'Article' },
                    { id: 'taxableAmount', label: 'Taxable Amount' },
                    { id: 'billAmount', label: 'Bill Amount' },
                    { id: 'gst', label: 'GST' }, // Combined column
                    { id: 'ewayBill', label: 'E-Way Bill' },
                    { id: 'actions', label: 'Actions' },
                  ].map((col) => (
                    <TableCell key={col.id}>
                      {['actions', 'gst'].includes(col.id) ? (
                        col.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'desc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ color: 'inherit', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row, index) => (
                  <TableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{row.userCode}</TableCell>
                    <TableCell>{String(row.billDate || '').slice(0, 10)}</TableCell>
                    <TableCell>{row.invoiceNumber}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>{row.article}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>₹{row.taxableAmount}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>₹{row.billAmount}</TableCell>
                    <TableCell>
                      <Box>
                        {Number(row.cgst) > 0 && <Typography variant="caption" display="block">CGST: ₹{row.cgst}</Typography>}
                        {Number(row.sgst) > 0 && <Typography variant="caption" display="block">SGST: ₹{row.sgst}</Typography>}
                        {Number(row.igst) > 0 && <Typography variant="caption" display="block">IGST: ₹{row.igst}</Typography>}
                        {Number(row.cgst) === 0 && Number(row.igst) === 0 && '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.ewayBill === 'Required' ? (
                        <Chip label="Required" color="warning" size="small" sx={{ fontWeight: 'bold' }} />
                      ) : (
                        <Chip label="Not Required" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                       <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" color="primary" onClick={() => setViewRecord(row)}><ViewIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="secondary" onClick={() => setEditRecord(row)} disabled={!canUpdate}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)} disabled={!canDelete}><DeleteIcon fontSize="small" /></IconButton>
                       </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>

        <ViewModal />
        <EditModal />
        <ExportOtpDialog
          open={otpDialogOpen}
          onClose={() => setOtpDialogOpen(false)}
          onSuccess={onOtpSuccess}
        />
      </Box>
    </Layout>
  );
}
